import { useCallback } from "react";
import { encryptUrlData } from "../../utils/encryption/CryptoService";
import { usePostFileDataBlobMutation, useLazyGetFileDataQuery } from "../../app/services/fileUploadApi";
import { downloadCopilotExport } from "./copilotExport";
import { pick } from "./copilotUtils";

const OPENING_PREFILL_KEY = "recruitmentCopilotOpeningPrefill";

const resolveAppUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${window.location.origin}${normalized}`;
};

const openInNewWindow = (path) => {
  const url = resolveAppUrl(path);
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

export const useCopilotActions = ({ runSuggestion }) => {
  const [downloadFileBlob] = usePostFileDataBlobMutation();
  const [getFileData] = useLazyGetFileDataQuery();

  const onNavigate = useCallback((path) => {
    if (!path) return;
    if (path.startsWith("/applicant-details/")) {
      const url = new URL(path, window.location.origin);
      const id = url.pathname.split("/").filter(Boolean).pop();
      if (!id) return;
      const encrypted =
        process.env.REACT_APP_IS_ENCRYPTION === "true" ? encryptUrlData(id) : id;
      openInNewWindow(`/applicant-details/${encrypted}${url.search}`);
      return;
    }
    // Talent CRM detail always uses encrypted route tokens (same as TalentCrm list).
    if (path.startsWith("/talent-crm/")) {
      const url = new URL(path, window.location.origin);
      const segments = url.pathname.split("/").filter(Boolean);
      const rawId = segments[1];
      if (rawId && rawId !== "metrics") {
        const alreadyEncrypted = !/^\d+$/.test(rawId);
        const token = alreadyEncrypted ? rawId : encryptUrlData(String(rawId));
        openInNewWindow(`/talent-crm/${token}${url.search}`);
        return;
      }
    }
    if (path.startsWith("/job-opening")) {
      const url = new URL(path, window.location.origin);
      const editId = url.searchParams.get("editId") || url.searchParams.get("jobOpeningId");
      if (editId && process.env.REACT_APP_IS_ENCRYPTION === "true") {
        url.searchParams.set("editId", encryptUrlData(String(editId)));
        url.searchParams.delete("jobOpeningId");
      }
      openInNewWindow(`${url.pathname}${url.search}`);
      return;
    }
    openInNewWindow(path);
  }, []);

  const onAction = useCallback(
    (action) => {
      const type = (pick(action, "type", "Type") || "navigate").toLowerCase();
      const path = pick(action, "path", "Path") || "";
      const payload = pick(action, "payload", "Payload") || {};
      const label = pick(action, "label", "Label");

      if (type === "prompt") {
        const prompt = payload.prompt || payload.Prompt || label;
        if (!prompt) return;
        runSuggestion(prompt);
        return;
      }

      if (type === "copy") {
        import("./CopilotComposer").then(({ copyCopilotText }) =>
          copyCopilotText(payload.text || payload.Text || label)
        );
        return;
      }

      if (type === "download") {
        downloadCopilotExport(action, {
          postFileData: downloadFileBlob,
          getFileData,
        });
        return;
      }

      if (!path) return;

      if (
        path === "/job-opening" &&
        (payload.jobTitle ||
          payload.JobTitle ||
          payload.intent === "draft_opening" ||
          payload.Intent === "draft_opening")
      ) {
        localStorage.setItem(OPENING_PREFILL_KEY, JSON.stringify(payload));
      }

      onNavigate(path);
    },
    [onNavigate, runSuggestion, downloadFileBlob, getFileData]
  );

  return { onNavigate, onAction };
};
